import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getConfig } from '@edx/frontend-platform';

import {
  getSubscriptionPlans,
  getMySubscriptionsForProduct,
  startSubscription,
  getBillingPortalUrl,
} from './api';

const ACTIVE_STATUSES = ['active', 'trialing'];

/**
 * Subscription state + actions for one product (course or program).
 *
 * Returns all available plans (one per track x interval cell), the learner's
 * current subscription (if any), and subscribe(mode, interval) / manage-billing
 * actions that redirect to the hosted Stripe pages. The caller (PurchasePanel)
 * scopes the plans to the selected enrollment track.
 */
export const useProductSubscription = ({
  productType, courseId, programId, enabled = true,
}) => {
  const productId = productType === 'program' ? programId : courseId;
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(null);

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['subscriptionPlans', productType, productId],
    queryFn: () => getSubscriptionPlans({ productType, courseId, programId }),
    enabled: Boolean(enabled && productId),
    staleTime: 10 * 60 * 1000,
  });

  const { data: subscriptions, isLoading: subsLoading } = useQuery({
    queryKey: ['mySubscription', productType, productId],
    queryFn: () => getMySubscriptionsForProduct({ productType, courseId, programId }),
    enabled: Boolean(enabled && productId),
    staleTime: 60 * 1000,
  });

  const availablePlans = useMemo(() => plans || [], [plans]);

  const activeSubscription = (subscriptions || []).find(
    (s) => ACTIVE_STATUSES.includes(s.status),
  ) || null;

  const redirectTo = async (fn) => {
    setError(null);
    setRedirecting(true);
    try {
      const origin = typeof window !== 'undefined' ? window.location.href : getConfig().LMS_BASE_URL;
      const url = await fn(origin);
      if (url) {
        window.location.href = url;
        return;
      }
      setRedirecting(false);
      setError('no_url');
    } catch (e) {
      setRedirecting(false);
      setError('action_failed');
    }
  };

  // Subscribe to a specific plan cell (enrollment track + billing interval).
  const subscribe = (mode = 'honor', interval = 'month') => redirectTo(async (origin) => {
    const result = await startSubscription({
      productType, courseId, programId, interval, mode, successUrl: origin, cancelUrl: origin,
    });
    return result.checkoutUrl;
  });

  const manageBilling = () => redirectTo((origin) => getBillingPortalUrl({ returnUrl: origin }));

  return {
    plans: availablePlans,
    isSubscribable: availablePlans.length > 0,
    activeSubscription,
    isSubscribed: Boolean(activeSubscription),
    loading: plansLoading || subsLoading,
    redirecting,
    error,
    subscribe,
    manageBilling,
  };
};
